/**
 * `AIContextEngineModel.KnowledgeTransport.ts` — port of Unity's
 * `AI/AIContextEngineModel.KnowledgeTransport.cs`, the **KnowledgeContextAgent**:
 * a gpt-5.5 Responses call carrying a project-scoped `file_search` tool over
 * the customer's OpenAI vector store.
 *
 * TypeScript has no partial classes, so — like the sibling
 * `AIContextEngineModel.SceneProjectUserContext.ts` — this exports free
 * functions over a small deps object, and `AIContextEngineModel.Core.ts`
 * delegates to them.
 *
 * **The cheap path first.** Before spending a model call, a prompt that names
 * a document (or reads like "open / show / where is …") is answered from the
 * docs MANIFEST by string matching alone (`searchDocsByFileNameAsync`, and the
 * manifest pre-pass inside `queryKnowledgeAsync`). That is Unity's design and
 * it is why `KnowledgeContextResponse.usedStringSearch` exists — the tool card
 * reports "string search" vs "semantic search" from it.
 *
 * **Deviations, each with its reason:**
 *  - Manifest access goes through `IProjectDocsModel` rather than a second
 *    private parser + cache. Unity keeps a duplicate DTO and a separate 30 s
 *    static cache here; one owner is strictly better and the TTL is preserved
 *    on the model.
 *  - `AIResponsesRequestTracker` (cancel-all-in-flight) is not ported: the web
 *    has no equivalent global cancel point today, and every call already takes
 *    an `AbortSignal`.
 *  - Reasoning effort is `"low"` rather than Unity's configurable
 *    `AIResponsesReasoningEffort` (default `"none"`), matching the choice the
 *    Workflow Builder agent already made on this platform.
 */

import { getAiConfig } from "@ai/aiConfig";
import type { ICustomerVectorStoreModel } from "@ai/memory/ICustomerVectorStoreModel";
import { K_RETRIEVAL_SCORE_FLOOR } from "@ai/memory/ICustomerVectorStoreModel";
import {
  extractMessageOnlyText,
  postResponsesAsync,
  tryParseResponsesUsage,
} from "@ai/subagents/aiResponsesUtility";
import type { UsageDelta } from "@ai/observability/AISessionUsageTracker";
import { DOC_EXTENSIONS, isPdfNameOrPath } from "@models/data/docsManifestDto";
import type { DocLibraryItem, IProjectDocsModel } from "@models/docs/IProjectDocsModel";

/** Port of Unity's `AI/UI/AIFileCitation.cs`. */
export interface AIFileCitation {
  readonly fileId: string;
  readonly fileName: string | null;
  readonly storagePath: string | null;
}

/**
 * A document `file_search` matched, carrying the BEST score any of its chunks
 * scored. Web-only: Unity keeps no scored view of the result set.
 */
export interface RetrievedDocument {
  readonly fileId: string;
  readonly fileName: string | null;
  readonly storagePath: string | null;
  /** Best chunk score, or null when the API reported none. */
  readonly score: number | null;
}

/** Port of Unity's `KnowledgeContextResponse` (`KnowledgeTransport.cs:17-31`). */
export interface KnowledgeContextResponse {
  readonly text: string;
  /**
   * The documents the sub-agent CITED. Unchanged Unity semantics — annotations
   * when it emitted any, else the raw hits. `Find_and_Show_PDF` reads this and
   * treats "more than one" as ambiguity, so it must stay the cited set.
   */
  readonly citations: ReadonlyArray<AIFileCitation> | null;
  /**
   * The documents to DISPLAY as the answer's sources: cited first, then every
   * other document the search matched that clears the relevance floor. Null on
   * the manifest paths, which run no search — fall back to `citations` there.
   */
  readonly sources: ReadonlyArray<AIFileCitation> | null;
  /**
   * True when a `file_search` call actually ran in this response.
   *
   * `sources` is null for TWO different reasons - no search ran (the manifest
   * paths and the error paths), or a search ran and matched nothing above the
   * floor. A caller that cannot tell them apart falls back to `citations`,
   * which with no annotations is the RAW UNFILTERED hit list: that republishes
   * to the user exactly what the floor just rejected. Read this before reading
   * `sources`.
   */
  readonly searchRan: boolean;
  /** True when the answer came from the manifest, with no model call. */
  readonly usedStringSearch: boolean;
  readonly usage: UsageDelta | null;
}

/** What the transport needs from its host, resolved lazily so AI-off is inert. */
export interface KnowledgeDeps {
  readonly docs: () => IProjectDocsModel | null;
  readonly vectorStore: () => ICustomerVectorStoreModel | null;
  readonly customerId: () => string | null;
  readonly productKey: () => string | null;
  readonly workflowId: () => string | null;
}

/** Unity `KnowledgeModelName` (`:35`). */
const K_KNOWLEDGE_MODEL = "gpt-5.5";
/** Unity `KnowledgeRequestTimeoutSeconds = 60` (`:41`). */
const K_REQUEST_TIMEOUT_MS = 60_000;
/**
 * A matched-but-uncited document is only offered as a source when its BEST
 * chunk clears this. Measured against the real customer store: genuine hits for
 * a question score 0.81-0.97, while the unrelated documents `file_search` pads
 * the result set with score 0.10-0.20 (a robot-cell guarding spec scores 0.1941
 * for "maximum run speed"). The empty band is [0.13, 0.81].
 *
 * THERE ARE TWO FLOORS AND THIS IS THE HIGHER ONE. The other is
 * `K_RETRIEVAL_SCORE_FLOOR` (0.5), sent to `file_search` as
 * `ranking_options.score_threshold`, deciding what the sub-agent may READ.
 * This one decides which merely-matched document is DISPLAYED as a source.
 *
 * They MUST differ, and this one must be the higher: once retrieval filters at
 * 0.5, a display floor of 0.5 can never fire again - it would be dead code. The
 * direction follows from the costs. A too-high RETRIEVAL floor refuses a
 * question the documents answer, which is severe. A too-high DISPLAY floor only
 * shows fewer corroborating chips, and a document the model actually CITED is
 * exempt from filtering either way, so the answer's own source is never hidden
 * by it. The cheaper failure mode gets the stricter setting.
 *
 * 0.6 keeps a real gap from the retrieval floor while staying 0.21 below the
 * measured hit floor (0.81), so a genuinely corroborating document still shows.
 *
 * ABSOLUTE, never relative to the top hit. A relative cutoff collapses in the
 * one case that matters — ask a question nothing answers, top score 0.15, and a
 * "half the top" bar of 0.075 turns every noise chunk into a source under an
 * answer that has none. Only a floor can correctly return nothing.
 *
 * Calibrated from one question against one two-document project. Dropped files
 * are logged so the number can be re-tuned against real traffic.
 */
const K_MIN_RETRIEVED_SOURCE_SCORE = 0.6;

/** Unity's `maxResults` ceiling on the source list (`ExtractFileCitations`). */
const K_MAX_SOURCES = 12;

/** Unity `AIContextEngineModel.EmptyPartHierarchyContext` (`Core.cs:14`). */
export const K_EMPTY_PART_HIERARCHY_CONTEXT = "Part Hierarchy: (empty)";

/** Unity `KnowledgeSystemPrompt` (`:65-80`) — verbatim. */
export const K_KNOWLEDGE_SYSTEM_PROMPT =
  "You are KnowledgeContextAgent. You receive a user prompt from another agent and a part context list.\n"
  + "Your job is to retrieve relevant information from documents in storage and return only what is needed to satisfy the prompt.\n"
  + "\n"
  + "Rules:\n"
  + "- Use only the provided part context and document search results. Never invent.\n"
  + "- If file_search is available, ALWAYS call it before answering and incorporate relevant results.\n"
  + "- Prioritize document findings when available; use part context details and notes to clarify or disambiguate.\n"
  + "- Respond concisely to the prompt using only relevant details.\n"
  + "- Preserve explicit numeric values exactly as given when documents or part context provide a concrete number, including the original units.\n"
  + "- When documents provide a concrete spec, prefer the document value and keep the same unit representation used in the docs instead of converting it to a different unit.\n"
  + "- Do not convert document measurements between meters, millimeters, centimeters, feet, inches, or other units unless the prompt explicitly asks for a conversion.\n"
  + '- Do not rewrite explicit numeric values with hedging like "about", "approximately", "around", or "roughly" unless the source itself marks the value as approximate, estimated, rounded, or a range.\n'
  + '- If the prompt asks for a count or yes/no, respond with a concise answer (e.g., "count: 12" or "yes").\n'
  // Web divergence from the Unity port, added 2026-08-27. RETRIEVAL ANSWERS A
  // SIMILARITY QUESTION; a user asking for a named artifact is asking an
  // EXISTENCE question, and nothing above told these two apart — so a search for
  // a spec the library does not carry returned the neighbouring figures, and the
  // answer presented them under the name that was asked for. Only this agent can
  // make the distinction: the calling agent never sees the documents, just this
  // prose, so a rule anywhere upstream is being applied to a decision already made.
  + "- Answer the question asked, not the nearest thing retrieval returned. Search finds text that is merely SIMILAR; content from the same subject area is not an answer to a question it does not address.\n"
  // Same divergence, extended 2026-09-04. The rule above turns on whether the
  // text ADDRESSES the question; this one on whether it is about the SUBJECT
  // named. A manual for one product answers "how fast can X run" perfectly - it
  // is simply about a different X - so the rule above lets it through, and the
  // figure crosses over reported under the name that was asked for.
  + '- If the prompt NAMES a subject — a product, model or machine — do not answer it from text about a DIFFERENT one, however closely the two are named and however well that text fits the question: never carry a figure across from one subject to another. Text about a whole machine IS about its parts, so a passage covering the assembly may answer a question about a part of it. If everything retrieved is about a different subject, respond "No relevant information found."\n'
  // Same divergence, extended 2026-09-06. ⚠ THIS PROMPT IS A ONE-SIDED
  // RATCHET ON NAMES: every rule that mentions one REMOVES it (never invent;
  // the two rules below, never name a document the results do not), NOT ONE
  // requires the answer to name anything, brevity is pressed three times, and
  // the only worked exemplars above ("count: 12", "yes") are subject-less. So
  // when the asked-for subject and the retrieved one disagree, naming EITHER is
  // blocked and the model names NEITHER — a bare figure is this prompt’s local
  // optimum, and it is worse than both alternatives: it strips the one datum
  // the CALLING agent’s own refusal rule keys on (skills/answer.md, “if nothing
  // comes back about the subject the user named, refuse”), so the figure reaches
  // the user under whatever name was asked, wearing the other subject’s citation.
  // ⚠ IT ADDS NO REFUSAL PATH, DELIBERATELY — that is the whole shape of it. The
  // rule above already forbids the crossing and did not bind, so a third rule of
  // the same family is a bet; the cheap explanation for why the two below DO bind
  // is that a prohibition on naming is obeyed by OMITTING, which is free, while
  // refusing abandons the answer under `verbosity: "low"`. Naming is free too, so
  // it is predicted to bind — and a rule that can only change an answer’s WORDING
  // cannot produce the failure that is unmissable in front of a customer.
  // Refusing stays the rule above’s alone; the last two clauses here are the
  // valves that keep it that way (an unnameable subject, and an answer that IS a
  // document — Find_and_Show_PDF shares this prompt and its answer is a filename).
  + '- When the prompt names a subject, NAME the subject your answer is about, taking the name from the retrieved text itself: "<subject>’s rated load is <value>", not a bare "<value>". A value with no subject attached reads to the agent that called you as an answer about whatever IT asked, which is how a figure crosses from one subject to another without either agent noticing; the concise forms above carry the name too. This only ADDS a name — it never withholds an answer, and it never overrides the two rules below about naming documents. If the retrieved text names no subject of its own, answer without one rather than inventing a name; and when the answer IS a document or a filename, that name is the answer.\n'
  + '- Never present retrieved content as a document, spec, sheet, section or procedure that the results do not themselves name. If the prompt asks for "the X spec" and no result identifies itself as that, the answer is "No relevant information found." — never the nearest figures reported under that name.\n'
  + "- Never state what a document is called, is labelled, or contains as a named section unless that wording appears in the retrieved text. Do not infer a heading from the figures beneath it.\n"
  + '- If no relevant information is found, respond with "No relevant information found."\n'
  + "- Do not include extra commentary, headings, or reasoning.\n";

/**
 * The exact sentence the system prompt above mandates for a miss. Kept beside
 * it so the string and the instruction that produces it cannot drift.
 *
 * A NOT-FOUND ANSWER HAS NO SOURCES. A citation attributes a claim, and this
 * text makes none - chips under it assert that these documents support an
 * answer that does not exist. The relevance floor cannot settle this on its
 * own: a document can clear it (or be admitted by the deliberate fail-open on
 * a missing score) while containing nothing that answers the question.
 */
const K_NOT_FOUND_ANSWER = "No relevant information found.";

/**
 * The prompt above is the sentence's only other home, and until the backstop
 * existed nothing in code ever EMITTED it - the constant was read-only, used to
 * recognise what the model wrote. Now that code produces it too, a prompt edit
 * rewording the sentence would leave the two disagreeing with no build error,
 * so say so at load. Same shape as `agentPrompts.ts`'s refusal drift warning,
 * and for the same reason.
 */
if (!K_KNOWLEDGE_SYSTEM_PROMPT.includes(K_NOT_FOUND_ANSWER)) {
  console.warn(
    `[KnowledgeContextAgent] the system prompt no longer mandates "${K_NOT_FOUND_ANSWER}" `
    + "verbatim; the not-found backstop and the prompt have drifted apart.",
  );
}

/** Normalized form of the sentence above, DERIVED so the two cannot drift. */
const K_NO_RELEVANT_INFO = K_NOT_FOUND_ANSWER.trim()
  .toLowerCase()
  .replace(/\s+/g, " ")
  .replace(/[.!]+$/, "");

/**
 * Whole-message match after normalizing case, whitespace and trailing
 * punctuation - deliberately NOT a substring test. An answer that legitimately
 * opens "No relevant information found for X, but Y states..." carries a real
 * claim and must keep its sources; wrongly suppressing those is worse than
 * missing a reworded miss.
 */
function isNotFoundAnswer(text: string): boolean {
  const normalized = text.trim().toLowerCase().replace(/\s+/g, " ").replace(/[.!]+$/, "");
  return normalized === K_NO_RELEVANT_INFO;
}

// ---- Public API -----------------------------------------------------

/**
 * Unity `BuildDocsPrompt` (`Core.cs:78-87`) — appends the docs instruction
 * unless the prompt already mentions documents or `file_search`.
 */
export function buildDocsPrompt(prompt: string): string {
  if (!prompt?.trim()) return prompt;
  const trimmed = prompt.trim();
  return trimmed + buildDocsInstruction(trimmed);
}

/** Unity `BuildDocsInstruction` (`Core.cs:279-293`). */
export function buildDocsInstruction(prompt: string): string {
  if (!prompt?.trim()) return "";
  const lower = prompt.toLowerCase();
  if (lower.includes("file_search") || lower.includes("doc")) return "";
  return " | Docs: search project documents for relevant info and include any findings";
}

/**
 * Unity `QueryInternalAsync` (`:131-245`).
 *
 * `semanticOnly` maps to Unity's `allowManifestOnlyFileSearch: false`: the
 * cheap manifest pre-pass that can answer without any model call is skipped,
 * and the manifest is used only to narrow `file_search` to one file name.
 */
export async function queryKnowledgeAsync(
  deps: KnowledgeDeps,
  partContext: string,
  prompt: string,
  semanticOnly: boolean,
  signal?: AbortSignal,
  /**
   * The not-found backstop below is scoped by `partContext`, and TWO callers
   * pass the empty sentinel - `Prompt_Search_Docs`, which it is for, and
   * `findPdfAsync`, which it breaks. That one is not asking a question the
   * documents must ANSWER; it is resolving WHICH document to open, and both of
   * its recovery channels are the very fields the backstop nulls, so a located
   * PDF came back as `pdf_not_found`. A shared data value is not an intent
   * signal - callers say what they want explicitly.
   */
  applyNotFoundBackstop = true,
): Promise<KnowledgeContextResponse> {
  if (!partContext?.trim()) {
    return response("Part context is empty.");
  }
  if (!prompt?.trim()) {
    // An empty prompt means "give me everything you already have".
    return response(partContext);
  }

  let restrictToFileName: string | null = null;
  if (!semanticOnly) {
    const filenameSearch = await tryResolveDocsByFileNameAsync(deps, prompt);
    if (filenameSearch) {
      if (filenameSearch.shouldReturnWithoutLlm) {
        return response(filenameSearch.text ?? "", filenameSearch.citations, true);
      }
      restrictToFileName = filenameSearch.restrictToFileName;
    }
  } else {
    restrictToFileName = await tryGetSemanticRestrictedFileNameAsync(deps, prompt);
  }

  if (!getAiConfig().responsesAuthAvailable) {
    return response("error: knowledge_context_agent_api_key_unavailable");
  }

  const fileSearchTool = await tryBuildKnowledgeFileSearchToolAsync(deps, restrictToFileName, signal);
  if (!fileSearchTool) {
    return response("No matching documents found.");
  }
  const body = buildRequestPayload(
    buildKnowledgeInputText(partContext, prompt),
    fileSearchTool,
  );

  let root: Record<string, unknown>;
  try {
    root = await postResponsesAsync(body, signal, K_REQUEST_TIMEOUT_MS);
  } catch (err) {
    if (signal?.aborted) return response("error: knowledge_context_agent_cancelled");
    const detail = err instanceof Error ? err.message : String(err);

    // `ranking_options` carries the retrieval floor, and it has a history of
    // being rejected by this endpoint contrary to the docs. Verified accepted
    // on 2026-08-28, but a regression there would take EVERY document search
    // down rather than just the floor - so drop it and retry once, the same
    // shape `ResponsesTurnBridge` uses for a rejected `reasoning` key.
    //
    // Losing the floor does NOT lose the guarantee: the backstop refuses on the
    // ranked sources, which are floored client-side either way. What is lost is
    // only that the sub-agent reads noise it should never have seen.
    const retryBody = tryDropRankingOptions(body, detail);
    if (!retryBody) {
      console.warn("[KnowledgeContextAgent] request failed:", detail);
      return response(`error: knowledge_context_agent_request_failed - ${detail}`);
    }
    console.warn(
      "[KnowledgeContextAgent] endpoint rejected ranking_options; retrying without the "
      + "retrieval floor:",
      detail,
    );
    try {
      root = await postResponsesAsync(retryBody, signal, K_REQUEST_TIMEOUT_MS);
    } catch (retryErr) {
      if (signal?.aborted) return response("error: knowledge_context_agent_cancelled");
      const retryDetail = retryErr instanceof Error ? retryErr.message : String(retryErr);
      console.warn("[KnowledgeContextAgent] request failed:", retryDetail);
      return response(`error: knowledge_context_agent_request_failed - ${retryDetail}`);
    }
  }

  const usage = tryParseResponsesUsage(root, K_KNOWLEDGE_MODEL);
  const citations = extractFileCitations(root);
  // The sub-agent annotates the document it quoted and routinely leaves the
  // others unmarked, so the cited set is NOT the matched set — see
  // `buildRankedSources`.
  const annotated = extractAnnotatedFileCitations(root);
  const retrieved = extractRetrievedDocuments(root);
  const sources = buildRankedSources(annotated, retrieved);
  const searchRan = didRunFileSearch(root);

  // THE BACKSTOP. Without it, "the documents do not answer this" is a judgement
  // the model has to make and then phrase byte-exactly, twice over: this
  // sub-agent choosing the mandated sentence, and then the main agent turning
  // adjacent prose into its own refusal string. Both guards are whole-string
  // equality, so a softened miss defeats them in the same act that creates the
  // need for them. Here it becomes a FACT: a search ran, nothing it matched
  // cleared the relevance floor, so there is nothing to answer from - whatever
  // the model wrote came from somewhere other than the documents.
  //
  // The cited-document exemption is preserved and is why this reads `sources`
  // rather than counting hits: `buildRankedSources` prepends anything the model
  // CITED without score-filtering it, so a genuine answer that names its source
  // never reaches this branch. The fail-open on a missing score is preserved
  // for the same reason - if the API stops returning scores, everything clears
  // the floor and this stays silent rather than refusing everything.
  //
  // SCOPED TO THE DOCUMENT-SEARCH PATH. `Get_Part_Context` passes real part
  // context and the sub-agent is told to "use only the provided part context
  // and document search results", so an answer there can legitimately come from
  // part notes with no document behind it; firing on that path would suppress a
  // valid answer. Residual, stated rather than papered over: noise can still
  // reach that path's `Docs:` section as TEXT, though the floor and the
  // `searchRan` fix stop it being corroborated with chips.
  // ⚠ TESTS THE RETRIEVAL FLOOR, NEVER `sources`. `sources` is the DISPLAY
  // list, floored at 0.6, and the two floors deliberately differ - so a chunk
  // scoring in [0.5, 0.6) is one the sub-agent MAY read and answer from that
  // will never appear in `sources`. Keying on `sources` destroyed exactly those
  // answers: a real figure, read from a real document, replaced by a refusal.
  // The separation of the floors is the whole design, and conflating them here
  // undid it. Ask what the model COULD have answered from, not what we would
  // have shown.
  //
  // Client-side rather than trusting the server floor, because the self-heal
  // above can legitimately drop `ranking_options` and let the noise back in -
  // and then this is the only thing standing between it and the user. Fails
  // OPEN on a missing score, exactly like `isRelevantEnough`.
  const readable = (retrieved ?? []).filter((doc) => clearsRetrievalFloor(doc.score));
  const nothingToAnswerFrom = readable.length === 0 && !annotated?.length;
  const isDocumentSearch = applyNotFoundBackstop
    && partContext.trim() === K_EMPTY_PART_HIERARCHY_CONTEXT;
  if (searchRan && nothingToAnswerFrom && isDocumentSearch) {
    console.debug(
      "[KnowledgeContextAgent] search matched nothing above the relevance floor; "
      + "answering not-found regardless of the model's wording.",
    );
    return response(K_NOT_FOUND_ANSWER, null, false, usage, null, true);
  }

  const outputText = extractMessageOnlyText(root);
  if (outputText) {
    const trimmed = outputText.trim();
    if (isNotFoundAnswer(trimmed)) {
      // Nothing was found, so nothing is attributed - see `isNotFoundAnswer`.
      if (sources?.length) {
        console.debug(
          `[KnowledgeContextAgent] not-found answer; withholding ${sources.length} matched source(s).`,
        );
      }
      return response(trimmed, null, false, usage, null, searchRan);
    }
    return response(trimmed, citations, false, usage, sources, searchRan);
  }
  if (citations && citations.length > 0) {
    return response(
      "error: knowledge_context_agent_empty_response",
      citations,
      false,
      usage,
      sources,
      searchRan,
    );
  }
  return response("error: knowledge_context_agent_parse_failed", null, false, usage);
}

/**
 * Unity `SearchDocsByFileNameInternalAsync` (`:97-129`) — manifest only, no
 * model call ever. Exact filename matches first, fuzzy name scoring second.
 */
export async function searchDocsByFileNameAsync(
  deps: KnowledgeDeps,
  prompt: string,
): Promise<KnowledgeContextResponse> {
  if (!prompt?.trim()) return response("error: prompt_required", null, true);

  const docs = await projectDocsAsync(deps);
  if (docs === null) return response("error: library_docs_unavailable", null, true);
  if (docs.length === 0) return response("No matching documents found in Library.", null, true);

  const explicitFileNames = extractExplicitDocFileNames(prompt);
  let matches = findDocsByExactFileName(docs, explicitFileNames);
  if (matches.length === 0) matches = findDocsByFuzzyName(docs, prompt, 8);

  // ⚠ "WHICH DOCUMENTS DO YOU HAVE?" IS NOT A NAME SEARCH, AND USED TO BE
  // UNANSWERABLE (AI-1360). The tool's own description invites the agent to
  // "find/list/confirm documents by name", but every way of ASKING for the list
  // is built from exactly the words `K_FUZZY_STOP_WORDS` removes — "list", "all",
  // "documents", "files", "what", "have" — so the term set came back empty, the
  // fuzzy pass returned nothing, and the library reported itself as empty. The
  // agent had no other route to the manifest (nothing injects it into context),
  // so it answered with the refusal string while holding a document it could
  // name perfectly well the moment the user guessed the name.
  //
  // A prompt with NO surviving terms is that request. It is distinguishable from
  // a search that genuinely missed — which has terms and simply did not match —
  // so the refusal below is kept for the real miss.
  if (matches.length === 0 && isDocListRequest(prompt)) {
    matches = docs.slice(0, K_MAX_LISTED_DOCS);
  }

  const found = buildDocsFoundResponse(matches);
  if (!found) return response("No matching documents found in Library.", null, true);
  return response(found.text ?? "", found.citations, true);
}

/**
 * Cap on a full-library listing. The whole list rides the turn as text AND as a
 * chip per document, so an unbounded one would both flood the panel and spend
 * the session's context on a question the user asked in passing.
 */
const K_MAX_LISTED_DOCS = 25;

/**
 * The prompt asks WHAT the Library holds, rather than for a document in it.
 *
 * The test is that nothing in the prompt NAMES anything: take the same terms
 * the fuzzy matcher would score with, then drop the generic vocabulary a person
 * builds an enumeration out of. A listing request is left with nothing, while
 * any request for a particular document keeps the word that identifies it —
 * "do you have a Turbophone manual" keeps `turbophone` and stays a search, and
 * "hydraulic schematic" keeps both words and still earns the honest refusal.
 *
 * ⚠ Deliberately a SECOND list rather than more entries in
 * `K_FUZZY_STOP_WORDS`. That set decides what a name search matches ON, so
 * putting "all" or "access" in it would quietly make a document called
 * `All Access Guide.pdf` unfindable by its own name. Nothing here reaches
 * scoring.
 */
function isDocListRequest(prompt: string): boolean {
  const terms = fuzzyNameTerms(prompt, extractQuotedPhrases(prompt));
  return terms.every((t) => K_LIST_REQUEST_FILLER.has(t.toLowerCase()));
}

/**
 * Words that carry no document name, so a prompt made only of these (plus the
 * locate vocabulary in `K_FUZZY_STOP_WORDS`) is asking for the whole list.
 * Covers how the question is actually put — "what documents do you have access
 * to", "list all the files", "is there anything in the library" — and the bare
 * "all documents" the tool description tells the agent to send.
 */
const K_LIST_REQUEST_FILLER = new Set([
  "all", "any", "anything", "everything", "available", "access", "accessible",
  "what", "which", "whats", "there", "have", "has", "got", "hold", "holds",
  "you", "your", "yours", "can", "see", "know", "about", "library", "loaded",
  "attached", "uploaded", "exist", "exists", "me", "us", "give", "tell", "read",
]);

/**
 * Unity `FindPdfInternalAsync` (`:247-428`) — resolve ONE pdf to open.
 * Manifest filename lookup first. An explicit `.pdf` fragment never falls
 * through to the LLM; only a topic query may use semantic search, and its
 * result is resolved back against the manifest before it can be opened.
 */
export async function findPdfAsync(
  deps: KnowledgeDeps,
  prompt: string,
  signal?: AbortSignal,
): Promise<KnowledgeContextResponse> {
  if (!prompt?.trim()) return response("error: prompt_required");
  const rawPrompt = prompt.trim();
  const requestedPdf = tryExtractPdfFileName(rawPrompt);

  const docs = await projectDocsAsync(deps);
  const pdfDocs = (docs ?? []).filter(
    (d) => isPdfNameOrPath(d.fileName) || isPdfNameOrPath(d.storagePath),
  );
  if (pdfDocs.length > 0) {
    // Exact match on an explicit filename is the strongest signal.
    if (requestedPdf) {
      const exact = findDocsByExactFileName(pdfDocs, [requestedPdf]);
      if (exact.length === 1) {
        return response(
          `ok: found ${exact[0].fileName.trim()}`,
          citationsFromDocs(exact),
          true,
        );
      }

      // A model can copy only the last whitespace-delimited token from a
      // filename returned by Search_Doc_By_File_Name (for example `1.pdf`
      // from `Robodog_Manual 1.pdf`). Resolve that fragment against the
      // authoritative manifest instead of spending an LLM call that can
      // invent `docs/1.pdf`.
      const suffixMatches = findDocsByFileNameSuffix(pdfDocs, requestedPdf);
      if (suffixMatches.length === 1) {
        const name = suffixMatches[0].fileName.trim();
        return response(`ok: found ${name}`, citationsFromDocs(suffixMatches), true);
      }
      if (suffixMatches.length > 1) {
        return response(
          `error: multiple_pdf_matches\n${suffixMatches.map((m) => m.fileName.trim()).join("\n")}`,
          citationsFromDocs(suffixMatches),
          true,
        );
      }

      // The MIRROR slip: the model INVENTED a name that is a word-bounded
      // prefix of a real one (`Robodog_Manual.pdf` for `Robodog_Manual 1.pdf`).
      // `Docs_Registry` invites exactly this - "Provide the filename if known
      // (e.g. 'manual.pdf')" - and the model cannot know a real file carries a
      // ` 1` before its extension. Manifest-only, like the suffix pass above.
      const stemMatches = findDocsByFileNameStemPrefix(pdfDocs, requestedPdf);
      if (stemMatches.length === 1) {
        const name = stemMatches[0].fileName.trim();
        return response(`ok: found ${name}`, citationsFromDocs(stemMatches), true);
      }
      if (stemMatches.length > 1) {
        return response(
          `error: multiple_pdf_matches\n${stemMatches.map((m) => m.fileName.trim()).join("\n")}`,
          citationsFromDocs(stemMatches),
          true,
        );
      }

      // An explicit filename that cannot be tied to a project document is not
      // a semantic-search question. Refuse it rather than asking the model to
      // manufacture an openable storage path.
      //
      // ⚠ IT MUST NOT FALL THROUGH TO `findDocsByFuzzyName`, which is the
      // tempting one-line fix and is the reason the three passes above are
      // written out instead. That scorer adds +3 for the prompt merely
      // CONTAINING the file's extension, independent of any name match
      // (`:944-948`) - and `score > 0` is its whole admission test. So
      // `zzzz.pdf` scores 3 against every PDF in the library and, in the
      // one-document library this bug was reported from, confidently opens
      // that document. MEASURED: "warranty" refuses, "warranty.pdf" opens the
      // manual. Turning a refusal into a wrong open is strictly worse than the
      // bug. That bonus is dead today only because the sole caller of fuzzy is
      // the branch where the prompt has NO extension.
      return response("error: pdf_not_found", null, true);
    }

    const matches = findDocsByFuzzyName(pdfDocs, rawPrompt, 8);
    if (matches.length > 0) {
      if (matches.length === 1) {
        const name = matches[0].fileName.trim() || "(pdf)";
        return response(`ok: found ${name}`, citationsFromDocs(matches), true);
      }
      return response(
        `error: multiple_pdf_matches\n${matches.map((m) => m.fileName.trim()).join("\n")}`,
        citationsFromDocs(matches),
        true,
      );
    }
  }

  if (docs !== null && requestedPdf) {
    return response("error: pdf_not_found", null, true);
  }

  // Fall back to the model when the filename lookup fails.
  const llm = await queryKnowledgeAsync(
    deps,
    K_EMPTY_PART_HIERARCHY_CONTEXT,
    buildFindPdfPromptForLlm(normalizePdfQuery(rawPrompt), requestedPdf),
    false,
    signal,
    // No not-found backstop: this lookup's ANSWER is a filename, carried in
    // exactly the two fields the backstop clears. Its own empty-candidate
    // branch below already returns `pdf_not_found` honestly.
    false,
  );

  let candidates: AIFileCitation[] = [];
  const namesFromText = extractPdfFileNamesFromText(llm.text);
  if (namesFromText.length > 0) {
    for (const name of namesFromText) {
      const fromCitation = llm.citations?.find(
        (c) => c.fileName?.trim().toLowerCase() === name.toLowerCase(),
      );
      if (docs === null) {
        if (fromCitation) candidates.push(fromCitation);
        continue;
      }

      const manifestMatches = findDocsByExactFileName(pdfDocs, [name]);
      const manifestCitations = citationsFromDocs(manifestMatches);
      if (manifestCitations) candidates.push(...manifestCitations);
    }
  } else if (llm.citations) {
    const pdfCitations = llm.citations.filter(
      (c) => isPdfNameOrPath(c.fileName ?? undefined) || isPdfNameOrPath(c.storagePath ?? undefined),
    );
    if (docs === null) {
      candidates = pdfCitations;
    } else {
      for (const citation of pdfCitations) {
        const manifestMatches = findDocsByExactFileName(
          pdfDocs,
          citation.fileName ? [citation.fileName] : [],
        );
        const manifestCitations = citationsFromDocs(manifestMatches);
        if (manifestCitations) candidates.push(...manifestCitations);
      }
    }
  }
  candidates = dedupeCitations(candidates);

  if (candidates.length === 0) {
    return response("error: pdf_not_found", null, llm.usedStringSearch, llm.usage);
  }
  if (requestedPdf) {
    const exact = candidates.find(
      (c) => c.fileName?.trim().toLowerCase() === requestedPdf.toLowerCase(),
    );
    if (exact) {
      return response(
        `ok: found ${exact.fileName?.trim()}`,
        [exact],
        llm.usedStringSearch,
        llm.usage,
      );
    }
    const list = candidates.map((c) => c.fileName?.trim()).filter(Boolean).join("\n");
    return response(
      list ? `error: pdf_not_found\n${list}` : "error: pdf_not_found",
      candidates,
      llm.usedStringSearch,
      llm.usage,
    );
  }
  if (candidates.length === 1) {
    return response(
      `ok: found ${candidates[0].fileName?.trim() || "(pdf)"}`,
      candidates,
      llm.usedStringSearch,
      llm.usage,
    );
  }
  return response(
    `error: multiple_pdf_matches\n${candidates.map((c) => c.fileName?.trim()).filter(Boolean).join("\n")}`,
    candidates,
    llm.usedStringSearch,
    llm.usage,
  );
}

// ---- Request assembly -----------------------------------------------

/**
 * A copy of `body` with `ranking_options` stripped from every tool, or null
 * when this failure was not about that key (so the caller reports the original
 * error rather than silently retrying an unrelated failure) or when there was
 * nothing to strip (so a persistent failure cannot retry forever).
 */
export function tryDropRankingOptions(
  body: Record<string, unknown>,
  errorDetail: string,
): Record<string, unknown> | null {
  if (!errorDetail.toLowerCase().includes("ranking_options")) return null;
  const tools = body.tools;
  if (!Array.isArray(tools)) return null;
  let stripped = false;
  const next = tools.map((tool) => {
    if (!isRecord(tool) || !("ranking_options" in tool)) return tool;
    stripped = true;
    const copy = { ...tool };
    delete copy.ranking_options;
    return copy;
  });
  return stripped ? { ...body, tools: next } : null;
}

/** Unity `BuildRequestPayload` (`:678-735`). */
function buildRequestPayload(
  inputText: string,
  fileSearchTool: Record<string, unknown> | null,
): Record<string, unknown> {
  const include: string[] = ["reasoning.encrypted_content", "web_search_call.action.sources"];
  const request: Record<string, unknown> = {
    model: K_KNOWLEDGE_MODEL,
    reasoning: { effort: "low", summary: null },
    input: [
      { role: "user", content: [{ type: "input_text", text: inputText }] },
    ],
    instructions: K_KNOWLEDGE_SYSTEM_PROMPT,
    text: { format: { type: "text" }, verbosity: "low" },
    store: false,
  };

  if (fileSearchTool) {
    request.tools = [fileSearchTool];
    request.tool_choice = "auto";
    include.push("file_search_call.results");
  } else {
    request.tools = [];
  }
  request.include = include;
  return request;
}

/** Unity `BuildKnowledgeInputText` (`:778-787`). */
function buildKnowledgeInputText(partContext: string, prompt: string): string {
  return `Prompt:\n${prompt.trim()}\n\nPart Context:\n${partContext.trim()}\n`;
}

async function tryBuildKnowledgeFileSearchToolAsync(
  deps: KnowledgeDeps,
  restrictToFileName: string | null,
  signal?: AbortSignal,
): Promise<Record<string, unknown> | null> {
  try {
    const docs = await projectDocsAsync(deps);
    if (!docs || docs.length === 0) return null;

    const customerId = deps.customerId();
    const productKey = deps.productKey();
    const workflowId = deps.workflowId();
    const vectorStore = deps.vectorStore();
    if (!customerId || (!productKey && !workflowId) || !vectorStore) return null;

    return await vectorStore.tryBuildWorkflowKnowledgeFileSearchToolAsync(
      customerId,
      {
        productKey,
        workflowId,
        restrictToFileName,
        maxNumResults: 6,
      },
      signal,
    );
  } catch (err) {
    console.warn("[KnowledgeContextAgent] failed to build file_search tool:", err);
    return null;
  }
}

// ---- Product-doc string search --------------------------------------

interface FilenameSearchResult {
  shouldReturnWithoutLlm: boolean;
  restrictToFileName: string | null;
  text?: string;
  citations?: ReadonlyArray<AIFileCitation> | null;
}

/**
 * `null` = the product-doc source could not be read;
 * `[]` = readable and empty.
 */
async function projectDocsAsync(
  deps: KnowledgeDeps,
): Promise<ReadonlyArray<DocLibraryItem> | null> {
  const docs = deps.docs();
  if (!docs) return null;
  try {
    return await docs.getProjectDocsAsync();
  } catch (err) {
    console.warn("[KnowledgeContextAgent] product docs read failed:", err);
    return null;
  }
}

/** Unity `TryResolveDocsByFileNameAsync` (`:889-946`). */
async function tryResolveDocsByFileNameAsync(
  deps: KnowledgeDeps,
  prompt: string,
): Promise<FilenameSearchResult | null> {
  if (!prompt?.trim()) return null;

  const isLocate = looksLikeDocLocateRequest(prompt);
  const explicitFileNames = extractExplicitDocFileNames(prompt);
  const hasExplicitFileName = explicitFileNames.length > 0;
  if (!isLocate && !hasExplicitFileName) return null;

  const docs = await projectDocsAsync(deps);
  if (!docs || docs.length === 0) return null;

  if (hasExplicitFileName) {
    const matches = findDocsByExactFileName(docs, explicitFileNames);
    const looksLikeContentQuestion = looksLikeDocContentQuestion(prompt, explicitFileNames);
    // "open manual.pdf" → answer from the manifest, no model call.
    if (!looksLikeContentQuestion && matches.length > 0) return buildDocsFoundResponse(matches);
    // "what does manual.pdf say about X" → one model call, restricted to it.
    if (looksLikeContentQuestion && matches.length === 1) {
      return { shouldReturnWithoutLlm: false, restrictToFileName: matches[0].fileName };
    }
    return null;
  }

  const matches = findDocsByFuzzyName(docs, prompt, 8);
  if (matches.length === 0) return null;
  return buildDocsFoundResponse(matches);
}

/** Unity `TryGetSemanticSearchRestrictedFileNameAsync` (`:866-887`). */
async function tryGetSemanticRestrictedFileNameAsync(
  deps: KnowledgeDeps,
  prompt: string,
): Promise<string | null> {
  if (!prompt?.trim()) return null;
  const explicitFileNames = extractExplicitDocFileNames(prompt);
  if (explicitFileNames.length === 0) return null;
  if (!looksLikeDocContentQuestion(prompt, explicitFileNames)) return null;

  const docs = await projectDocsAsync(deps);
  if (!docs || docs.length === 0) return null;
  const matches = findDocsByExactFileName(docs, explicitFileNames);
  return matches.length === 1 ? matches[0].fileName : null;
}

/** Unity `BuildDocsFoundResponse` (`:1179-1228`). */
function buildDocsFoundResponse(
  matches: ReadonlyArray<DocLibraryItem>,
): FilenameSearchResult | null {
  const citations = citationsFromDocs(matches);
  if (!citations || citations.length === 0) return null;
  const names = matches.map((m) => m.fileName).filter(Boolean);
  const text =
    names.length === 1
      ? `Found document: ${names[0]}`
      : `Found ${names.length} documents:\n${names.join("\n")}`;
  return { shouldReturnWithoutLlm: true, restrictToFileName: null, text, citations };
}

function citationsFromDocs(
  docs: ReadonlyArray<DocLibraryItem>,
): ReadonlyArray<AIFileCitation> | null {
  const citations: AIFileCitation[] = [];
  for (const doc of docs) {
    const fileName = doc.fileName?.trim();
    if (!fileName) continue;
    const storagePath = doc.storagePath?.trim() || `docs/${fileName}`;
    citations.push({ fileId: storagePath, fileName, storagePath });
  }
  return citations.length > 0 ? citations : null;
}

/** Unity `FindDocsByExactFileNameMatches` (`:948-986`). */
function findDocsByExactFileName(
  docs: ReadonlyArray<DocLibraryItem>,
  requestedFileNames: ReadonlyArray<string>,
): DocLibraryItem[] {
  const matches: DocLibraryItem[] = [];
  const seen = new Set<string>();
  for (const requested of requestedFileNames) {
    if (!requested?.trim()) continue;
    for (const doc of docs) {
      if (!doc.fileName || !doc.storagePath) continue;
      // ⚠ FILE NAME ONLY, deliberately. Every caller passes a dotted,
      // extension-bearing token (`extractExplicitDocFileNames` requires both,
      // `tryExtractPdfFileName` filters that same list, `extractPdfFileNamesFromText`
      // is a `\.pdf` regex, and the last passes a citation's raw `fileName`), so a
      // Define title can never appear here. Matching titles too would be dead for
      // every real title AND would make a title that equals another document's
      // file name match both, turning an unambiguous open into
      // `multiple_pdf_matches`. Opening by TITLE is `findDocsByFuzzyName`'s job.
      if (doc.fileName.toLowerCase() !== requested.toLowerCase()) continue;
      const key = doc.storagePath.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      matches.push(doc);
    }
  }
  return matches;
}

/**
 * Resolve an incomplete filename copied from a multi-word manifest entry.
 * Every match remains a real manifest row; callers must reject ambiguity.
 */
function findDocsByFileNameSuffix(
  docs: ReadonlyArray<DocLibraryItem>,
  requestedFileName: string,
): DocLibraryItem[] {
  const requested = requestedFileName?.trim().toLowerCase();
  if (!requested) return [];

  return docs.filter((doc) => {
    const fileName = doc.fileName?.trim().toLowerCase();
    if (!fileName?.endsWith(requested)) return false;
    const prefixLength = fileName.length - requested.length;
    return prefixLength === 0 || !/[\p{L}\p{N}]/u.test(fileName[prefixLength - 1] ?? "");
  });
}

/** Everything before the final extension, trimmed and lower-cased. */
function fileNameStem(name: string | undefined): string {
  const trimmed = name?.trim().toLowerCase() ?? "";
  const dot = trimmed.lastIndexOf(".");
  return dot > 0 ? trimmed.slice(0, dot) : trimmed;
}

/**
 * Resolve a filename the model INVENTED that is a word-bounded PREFIX of a real
 * one - the mirror of `findDocsByFileNameSuffix`, and it exists for the mirror
 * slip: that one recovers a trailing fragment the model copied (`1.pdf`), this
 * one recovers a leading stem the model guessed (`Robodog_Manual.pdf` for
 * `Robodog_Manual 1.pdf`).
 *
 * Same two guarantees as its mirror, and both are load-bearing: every match is
 * a real manifest row (nothing is synthesized), and callers MUST reject
 * ambiguity rather than picking one.
 *
 * ⚠ The word boundary is what keeps this from being a fuzzy match. Without it
 * `Robodog_Man.pdf` would resolve `Robodog_Manual 1.pdf`, i.e. a typo would
 * open a document with a different name. With it, only a name the requested one
 * genuinely STARTS can match.
 */
function findDocsByFileNameStemPrefix(
  docs: ReadonlyArray<DocLibraryItem>,
  requestedFileName: string,
): DocLibraryItem[] {
  const requested = fileNameStem(requestedFileName);
  if (!requested) return [];

  return docs.filter((doc) => {
    const stem = fileNameStem(doc.fileName);
    if (!stem.startsWith(requested)) return false;
    const next = stem[requested.length];
    return next === undefined || !/[\p{L}\p{N}]/u.test(next);
  });
}

/** Unity `FindDocsByFuzzyNameMatch` (`:1454-1559`) — quoted phrases weigh 100, bare tokens 10. */
function findDocsByFuzzyName(
  docs: ReadonlyArray<DocLibraryItem>,
  prompt: string,
  maxResults: number,
): DocLibraryItem[] {
  if (docs.length === 0 || !prompt?.trim()) return [];

  const phrases = extractQuotedPhrases(prompt);
  const terms = fuzzyNameTerms(prompt, phrases);
  if (terms.length === 0) return [];

  const promptLower = prompt.toLowerCase();
  const phraseSet = new Set(phrases);
  const scored: { doc: DocLibraryItem; score: number }[] = [];
  for (const doc of docs) {
    if (!doc.fileName) continue;
    const fileLower = doc.fileName.toLowerCase();
    // Score the DISPLAY title alongside the file name and take the better of
    // the two per term - a document whose title genuinely differs from its
    // file ("Safety Guide" / "SG_v4_final.pdf") is otherwise unreachable by
    // name, and would burn an LLM round trip that can answer pdf_not_found.
    const titleLower = doc.displayName.trim().toLowerCase();
    let score = 0;
    for (const term of terms) {
      const t = term.toLowerCase();
      const weight = phraseSet.has(term) ? 100 : 10;
      if (fileLower.includes(t) || (titleLower && titleLower.includes(t))) score += weight;
    }
    const dot = doc.fileName.lastIndexOf(".");
    if (dot > 0) {
      const ext = doc.fileName.slice(dot).toLowerCase();
      if (promptLower.includes(ext)) score += 3;
    }
    if (score > 0) scored.push({ doc, score });
  }
  if (scored.length === 0) return [];

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.doc.fileName.toLowerCase().localeCompare(b.doc.fileName.toLowerCase());
  });
  return scored.slice(0, Math.max(0, maxResults)).map((s) => s.doc);
}

/**
 * The words of a prompt that can actually MATCH a document name — quoted
 * phrases first, then bare tokens, with the locate vocabulary and anything
 * under three characters removed.
 *
 * Shared with `searchDocsByFileNameAsync`, which reads "no terms survived" as
 * the signal that the user asked to ENUMERATE rather than to find one file.
 * Both callers must decide that from the same list: a stop word added here and
 * not there would turn a real search into a full listing.
 */
function fuzzyNameTerms(prompt: string, phrases: ReadonlyArray<string>): string[] {
  const terms: string[] = [];
  for (const phrase of phrases) {
    if (phrase.length >= 3 && !K_FUZZY_STOP_WORDS.has(phrase.toLowerCase())) terms.push(phrase);
  }
  for (const token of extractAlphaNumericTokens(prompt)) {
    if (token.length >= 3 && !K_FUZZY_STOP_WORDS.has(token.toLowerCase())) terms.push(token);
  }
  return terms;
}

/** Unity's shared stop list for locate-style prompts (`:1463-1467`). */
const K_FUZZY_STOP_WORDS = new Set([
  "open", "show", "display", "view", "pull", "bring", "load", "locate", "where", "is", "list",
  "find", "get", "doc", "docs", "document", "documents", "file", "files", "pdf", "please",
  "the", "a", "an", "to", "for", "of", "in", "on", "and",
]);

/** Unity `LooksLikeDocLocateRequest` (`:1230-1274`). */
function looksLikeDocLocateRequest(prompt: string): boolean {
  const p = prompt?.trim();
  if (!p) return false;
  const lower = p.toLowerCase();
  if (
    lower.includes("open ")
    || lower.includes(" show ")
    || lower.startsWith("show ")
    || lower.includes(" display ")
    || lower.includes(" view ")
    || lower.includes(" pull up ")
    || lower.includes(" bring up ")
    || lower.includes(" load ")
    || lower.includes(" locate ")
    || lower.includes(" where is ")
    || lower.includes(" where's ")
    || ((lower.includes(" list ") || lower.startsWith("list "))
      && (lower.includes(" doc") || lower.includes(" document") || lower.includes(" file")))
    || lower.includes(" find document")
    || lower.includes(" find the document")
    || lower.includes(" find doc")
    || lower.includes(" find file")
    || lower.includes(" do we have")
    || lower.includes(" is there")
  ) {
    return true;
  }
  // A bare document name counts as a locate request.
  return extractExplicitDocFileNames(prompt).length > 0;
}

/** Unity `LooksLikeDocContentQuestion` (`:1276-1359`). */
function looksLikeDocContentQuestion(
  prompt: string,
  explicitFileNames: ReadonlyArray<string>,
): boolean {
  if (!prompt?.trim()) return false;
  const lower = prompt.toLowerCase();
  if (
    lower.includes("what ")
    || lower.includes("how ")
    || lower.includes("why ")
    || lower.includes("when ")
    || lower.includes("where ")
    || lower.includes("explain")
    || lower.includes("summarize")
    || lower.includes("according to")
    || lower.includes("does it say")
    || lower.includes("in the file")
    || lower.includes("in this file")
    || lower.includes("in the document")
    || lower.includes("in this document")
    || lower.includes("section")
    || lower.includes("page ")
    || lower.includes("quote")
  ) {
    return true;
  }

  const tokens = extractAlphaNumericTokens(prompt);
  if (tokens.length === 0) return false;

  // The file name itself (and its base + extension) are not "extra" tokens —
  // one surviving token of length >= 3 means the user asked something ABOUT it.
  const stop = new Set(K_CONTENT_STOP_WORDS);
  for (const fileName of explicitFileNames) {
    if (!fileName?.trim()) continue;
    stop.add(fileName.toLowerCase());
    const dot = fileName.lastIndexOf(".");
    if (dot > 0) {
      stop.add(fileName.slice(0, dot).toLowerCase());
      stop.add(fileName.slice(dot + 1).toLowerCase());
    }
  }
  return tokens.some((t) => t.length >= 3 && !stop.has(t.toLowerCase()));
}

/** Unity's wider stop list for the content-question test (`:1311-1316`). */
const K_CONTENT_STOP_WORDS = [
  "open", "show", "display", "view", "pull", "bring", "load", "locate", "where", "is", "list",
  "find", "get", "do", "does", "did", "we", "have", "has", "there", "any", "exists", "exist",
  "available", "doc", "docs", "document", "documents", "file", "files", "pdf", "please", "the",
  "a", "an", "to", "for", "of", "in", "on", "and",
];

/** Unity `ExtractExplicitDocFileNames` (`:1361-1408`). */
export function extractExplicitDocFileNames(prompt: string): string[] {
  const results: string[] = [];
  if (!prompt?.trim()) return results;
  const allowed = new Set(DOC_EXTENSIONS);

  for (const raw of prompt.split(/[\s\t\r\n]+/)) {
    if (!raw) continue;
    let token = trimOuterPunctuation(raw);
    while (token.endsWith(".") && token.length > 1) token = token.slice(0, -1);
    const dot = token.lastIndexOf(".");
    if (dot <= 0 || dot >= token.length - 1) continue;
    if (!allowed.has(token.slice(dot + 1).toLowerCase())) continue;
    results.push(token);
  }
  return results;
}

/** Unity `TrimOuterPunctuation` (`:1410-1418`). */
function trimOuterPunctuation(token: string): string {
  return token.trim().replace(/^["'`([{<,;:!?]+|["'`)\]}>,;:!?]+$/g, "");
}

/** Unity `ExtractAlphaNumericTokens` (`:1420-1452`). */
function extractAlphaNumericTokens(text: string): string[] {
  if (!text) return [];
  return text.match(/[\p{L}\p{N}]+/gu) ?? [];
}

/** Unity `ExtractKnowledgeQuotedPhrases` (`:1561-1594`). */
function extractQuotedPhrases(text: string): string[] {
  const phrases: string[] = [];
  if (!text) return phrases;
  for (let i = 0; i < text.length; i++) {
    const quote = text[i];
    if (quote !== '"' && quote !== "'") continue;
    const start = i + 1;
    const end = text.indexOf(quote, start);
    if (end <= start) continue;
    const phrase = text.slice(start, end).trim();
    if (phrase) phrases.push(phrase);
    i = end;
  }
  return phrases;
}

// ---- PDF helpers ----------------------------------------------------

function tryExtractPdfFileName(text: string): string | null {
  return extractExplicitDocFileNames(text).find((n) => isPdfNameOrPath(n)) ?? null;
}

function extractPdfFileNamesFromText(text: string): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  const names: string[] = [];
  for (const match of text.match(/[^\s"'`()[\]{}<>,;:!?]+\.pdf/giu) ?? []) {
    const key = match.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(match);
  }
  return names;
}

/** Strip the locate verbs so the model sees the subject, not the command. */
function normalizePdfQuery(prompt: string): string {
  return prompt
    .replace(/\b(open|show|display|view|pull up|bring up|load|locate|find)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Unity `BuildFindPdfPromptForLlm` (`:666-676`). */
function buildFindPdfPromptForLlm(query: string, requestedPdf: string | null): string {
  const safeQuery = query?.trim() || "(unspecified)";
  const requested = requestedPdf ? ` The user explicitly asked for "${requestedPdf}".` : "";
  return (
    "PDF lookup request. Identify the best matching PDF document in the project document library for this request: "
    + safeQuery
    + "."
    + requested
    + " Return ONLY the exact PDF filename(s) ending with .pdf, one per line. If no matching PDF is found, respond with 'No matching PDF found.'"
  );
}

function dedupeCitations(citations: ReadonlyArray<AIFileCitation>): AIFileCitation[] {
  const seen = new Set<string>();
  const result: AIFileCitation[] = [];
  for (const citation of citations) {
    const key = (citation.storagePath || citation.fileName || citation.fileId).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(citation);
  }
  return result;
}

// ---- Response parsing -----------------------------------------------

/**
 * Did a `file_search` call actually run in this response?
 *
 * Deliberately tests for the CALL, never for its results: with a retrieval
 * floor in place a search that matched nothing legitimately comes back with an
 * empty `results` array, and reading that as "no search ran" would put us
 * straight back into the fallback this exists to stop.
 */
export function didRunFileSearch(root: Record<string, unknown>): boolean {
  const output = root.output;
  if (!Array.isArray(output)) return false;
  return output.some((item) => isRecord(item) && item.type === "file_search_call");
}

/**
 * Unity `ExtractFileCitations` (`:1596-1712`): read `file_citation`
 * annotations off the message content, backfilling name/path from the
 * `file_search_call` results map, and falling back to those results outright
 * when the model emitted no annotations at all.
 */
export function extractFileCitations(
  root: Record<string, unknown>,
): ReadonlyArray<AIFileCitation> | null {
  const metadata = extractFileSearchResultMetadata(root);
  const output = root.output;
  if (!Array.isArray(output)) return null;

  const citations = annotationsToCitations(output, metadata);
  if (citations.length > 0) return dedupeCitations(citations);

  // No annotations: surface the raw file_search hits so the panel still shows
  // clickable sources (Unity's `maxResults: 12`).
  const fallback = [...metadata.entries()]
    .slice(0, K_MAX_SOURCES)
    .map(([fileId, meta]) => ({
      fileId,
      fileName: meta.fileName,
      storagePath: meta.storagePath ?? (meta.fileName ? `docs/${meta.fileName}` : null),
    }));
  return fallback.length > 0 ? dedupeCitations(fallback) : null;
}

/**
 * The documents the sub-agent explicitly cited — annotations ONLY, with no
 * fall back to the raw hits. `extractFileCitations` conflates the two because
 * Unity does; the ranked source list needs them apart, since a cited document
 * is never score-filtered and a merely-matched one is.
 */
export function extractAnnotatedFileCitations(
  root: Record<string, unknown>,
): ReadonlyArray<AIFileCitation> | null {
  const output = root.output;
  if (!Array.isArray(output)) return null;
  const citations = annotationsToCitations(output, extractFileSearchResultMetadata(root));
  return citations.length > 0 ? dedupeCitations(citations) : null;
}

/**
 * Every document `file_search` matched, in the order the API returned them,
 * each carrying its best chunk score. Ordering is left to the caller.
 */
export function extractRetrievedDocuments(
  root: Record<string, unknown>,
): ReadonlyArray<RetrievedDocument> | null {
  const metadata = extractFileSearchResultMetadata(root);
  if (metadata.size === 0) return null;
  return [...metadata.entries()].map(([fileId, meta]) => ({
    fileId,
    fileName: meta.fileName,
    storagePath: meta.storagePath,
    score: meta.score,
  }));
}

/**
 * The ranked source list shown under a document answer: **cited first, then
 * every other matched document by score**.
 *
 * Why the cited documents lead rather than the highest-scoring one. A score
 * grades how much a passage RESEMBLES the query — not which document is
 * authoritative. Two near-identical manuals where one supersedes the other
 * score within 0.4% of each other, and the superseded one can come out on top;
 * the sub-agent then correctly answers from the successor because that document
 * SAYS it supersedes the other. Sorting the list purely by score would put the
 * stale manual above an answer taken from the current one, which reads as a
 * mismatch. So the document the answer came from leads, and the rest follow by
 * best chunk score.
 *
 * A cited document is never score-filtered: the model asserting it answered the
 * question outranks a similarity number.
 */
export function buildRankedSources(
  annotated: ReadonlyArray<AIFileCitation> | null,
  retrieved: ReadonlyArray<RetrievedDocument> | null,
): ReadonlyArray<AIFileCitation> | null {
  const dropped: string[] = [];
  const rest = [...(retrieved ?? [])]
    .filter((doc) => {
      if (isRelevantEnough(doc.score)) return true;
      dropped.push(`${doc.fileName ?? doc.fileId} (${doc.score?.toFixed(4)})`);
      return false;
    })
    // Sort explicitly. Map insertion order happens to be score-descending for a
    // single search, but the extractor unions across every `file_search_call`
    // in the response and the model can issue more than one.
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .map(toCitation);

  const ordered = dedupeSources([...(annotated ?? []), ...rest]).slice(0, K_MAX_SOURCES);
  if (dropped.length > 0) {
    console.debug(
      `[KnowledgeContextAgent] below the ${K_MIN_RETRIEVED_SOURCE_SCORE} relevance floor, not offered as sources: ${dropped.join(", ")}`,
    );
  }
  return ordered.length > 0 ? ordered : null;
}

/**
 * Could the sub-agent legitimately have READ this chunk? The retrieval floor,
 * applied client-side so it still holds when the self-heal has had to drop the
 * server-side one. Same deliberate fail-open as `isRelevantEnough`.
 */
function clearsRetrievalFloor(score: number | null): boolean {
  if (typeof score !== "number" || !Number.isFinite(score)) return true;
  return score >= K_RETRIEVAL_SCORE_FLOOR;
}

/** Fail OPEN on a missing score — see the note on the constant. */
function isRelevantEnough(score: number | null): boolean {
  // Failing CLOSED here would silently reinstate the exact defect this fixes
  // the day the API renames the field: no error, no log, just a source list
  // back down to one. Do not "harden" this.
  if (typeof score !== "number" || !Number.isFinite(score)) return true;
  return score >= K_MIN_RETRIEVED_SOURCE_SCORE;
}

function toCitation(doc: RetrievedDocument): AIFileCitation {
  return {
    fileId: doc.fileId,
    fileName: doc.fileName,
    storagePath: doc.storagePath ?? (doc.fileName ? `docs/${doc.fileName}` : null),
  };
}

/**
 * Dedupe on file id AND storage path, unlike `dedupeCitations`, which keys on
 * one derived string. Here the same document can arrive twice from two places —
 * once as an annotation and once as a raw hit — and those two entries need only
 * agree on ONE of the two identifiers to be the same document.
 */
function dedupeSources(citations: ReadonlyArray<AIFileCitation>): AIFileCitation[] {
  const seenIds = new Set<string>();
  const seenPaths = new Set<string>();
  const result: AIFileCitation[] = [];
  for (const citation of citations) {
    const id = citation.fileId?.toLowerCase() ?? "";
    const path = (citation.storagePath || citation.fileName || "").toLowerCase();
    if ((id && seenIds.has(id)) || (path && seenPaths.has(path))) continue;
    if (id) seenIds.add(id);
    if (path) seenPaths.add(path);
    result.push(citation);
  }
  return result;
}

/** The annotation walk, shared by the two extractors above. */
function annotationsToCitations(
  output: ReadonlyArray<unknown>,
  metadata: Map<string, FileSearchResultMetadata>,
): AIFileCitation[] {
  const citations: AIFileCitation[] = [];
  for (const item of output) {
    if (!isRecord(item) || item.type !== "message") continue;
    const content = item.content;
    if (!Array.isArray(content)) continue;
    for (const part of content) {
      if (!isRecord(part)) continue;
      const annotations = part.annotations;
      if (!Array.isArray(annotations)) continue;
      for (const annotation of annotations) {
        if (!isRecord(annotation) || annotation.type !== "file_citation") continue;
        const fileId = firstString(annotation, "file_id", "fileId", "id");
        if (!fileId) continue;

        let fileName = firstString(annotation, "filename", "file_name", "fileName", "name");
        if (!fileName && isRecord(annotation.file)) {
          fileName = firstString(annotation.file, "filename", "file_name", "fileName", "name");
        }
        let storagePath = firstString(annotation, "storage_path", "storagePath");
        if (!storagePath && isRecord(annotation.attributes)) {
          storagePath = firstString(annotation.attributes, "storage_path", "storagePath");
        }

        const meta = metadata.get(fileId);
        if (meta) {
          fileName ??= meta.fileName;
          storagePath ??= meta.storagePath;
        }
        if (!storagePath && fileName) storagePath = `docs/${fileName}`;
        citations.push({ fileId, fileName, storagePath });
      }
    }
  }
  return citations;
}

interface FileSearchResultMetadata {
  fileName: string | null;
  storagePath: string | null;
  /** BEST chunk score for this file — see the aggregation note below. */
  score: number | null;
}

/** Unity `ExtractFileSearchResultMetadata` (`:1825-1897`). */
function extractFileSearchResultMetadata(
  root: Record<string, unknown>,
): Map<string, FileSearchResultMetadata> {
  const map = new Map<string, FileSearchResultMetadata>();
  const output = root.output;
  if (!Array.isArray(output)) return map;

  for (const item of output) {
    if (!isRecord(item) || item.type !== "file_search_call") continue;
    const results = item.results;
    if (!Array.isArray(results)) continue;
    for (const result of results) {
      if (!isRecord(result)) continue;
      const fileId = firstString(result, "file_id", "fileId", "id");
      if (!fileId) continue;
      let fileName = firstString(result, "filename", "file_name", "fileName", "name");
      let storagePath = firstString(result, "storage_path", "storagePath");
      if (isRecord(result.attributes)) {
        fileName ??= firstString(result.attributes, "filename", "file_name", "fileName");
        storagePath ??= firstString(result.attributes, "storage_path", "storagePath");
      }
      const rawScore = result.score;
      const score =
        typeof rawScore === "number" && Number.isFinite(rawScore) ? rawScore : null;
      const existing = map.get(fileId);
      map.set(fileId, {
        fileName: fileName ?? existing?.fileName ?? null,
        storagePath: storagePath ?? existing?.storagePath ?? null,
        // MAX, never the `??` last-wins the two lines above use. Results arrive
        // score-descending, so last-wins would record each file's WORST chunk —
        // and a file whose best chunk scored 0.96 would be filtered out on a
        // 0.09 tail chunk, silently reproducing the very bug this fixes.
        score:
          existing?.score == null
            ? score
            : score == null
              ? existing.score
              : Math.max(existing.score, score),
      });
    }
  }
  return map;
}

// ---- Small helpers --------------------------------------------------

function response(
  text: string,
  citations: ReadonlyArray<AIFileCitation> | null = null,
  usedStringSearch = false,
  usage: UsageDelta | null = null,
  sources: ReadonlyArray<AIFileCitation> | null = null,
  searchRan = false,
): KnowledgeContextResponse {
  return { text, citations, sources, usedStringSearch, usage, searchRan };
}

function firstString(
  source: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
